import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { NavLink, Link, useLocation } from "react-router-dom";

const links = [
  { label: "Home", to: "/" },
  { label: "Diensten", to: "/diensten" },
  { label: "Over Ons", to: "/over-ons" },
  { label: "Reviews", to: "/reviews" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200"
          : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group">
            <img
              src="https://customer-assets.emergentagent.com/job_dila-clean/artifacts/p08bjxwq_120d0b4e-e8a5-4be5-9790-b37db7897d9d.jpeg"
              alt="Dila Group BV — Schoonmaak & Services"
              className="h-11 sm:h-12 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `relative font-body text-sm font-medium transition-colors ${
                    isActive
                      ? "text-[#0284C7]"
                      : "text-slate-700 hover:text-[#0284C7]"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative inline-block py-1">
                    {l.label}
                    <span
                      className={`absolute -bottom-0.5 left-0 h-0.5 bg-[#0284C7] transition-all duration-300 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:0466324304"
              data-testid="navbar-phone"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F172A] hover:text-[#0284C7] transition-colors"
            >
              <Phone size={16} className="text-[#16A34A]" />
              0466 32 43 04
            </a>
            <Link
              to="/contact"
              data-testid="navbar-cta-quote"
              className="inline-flex items-center justify-center rounded-full bg-[#0284C7] hover:bg-[#0369A1] text-white text-sm font-semibold px-5 py-2.5 transition-all shadow-md shadow-sky-200 hover:shadow-lg hover:-translate-y-0.5"
            >
              Offerte aanvragen
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="navbar-mobile-toggle"
            className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-[#0F172A]"
            aria-label="Menu openen"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-6 pt-2 border-t border-slate-200">
            <div className="flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-lg text-base font-medium ${
                      isActive
                        ? "bg-sky-50 text-[#0284C7]"
                        : "text-slate-800 hover:bg-slate-100"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="navbar-mobile-cta"
                className="mt-3 inline-flex items-center justify-center rounded-full bg-[#0284C7] text-white text-sm font-semibold px-5 py-3"
              >
                Offerte aanvragen
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
